import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./apiClient";
import { fetchBillingMe } from "./sharedGet";
import { useAuth } from "../context/AuthContext";

// Feature flag keys — kept in sync with backend plans.py
export const FEATURES = {
  COMPLIANCE: "compliance",
  GRANTS: "grants",
  MENTOR: "mentor",
  HEALTH_CHECK: "health_check",
  FIND_SERVICES: "find_services",
  TEMPLATES_APPLY: "templates_apply",
  COLLABORATORS: "collaborators",
  ROADMAP: "roadmap",
  BUDGET: "budget",
  GOALS: "goals",
  INVESTOR_SCENARIOS: "investor_scenarios",
  PDF_EXPORT: "pdf_export",
  PITCH_DECK: "pitch_deck",
  SHARE: "share",
  BENCHMARKS: "benchmarks",
  FUNDING_STRATEGY: "funding_strategy",
  CAMPAIGNS: "campaigns",
  LENDER_PACKAGE: "lender_package",
  WHATIF: "whatif_scenarios",
  FEASIBILITY: "feasibility",
  OPTIMIZATION: "optimization",
  PROCESS_IMPROVEMENT: "process_improvement",
};

const SubscriptionContext = createContext({
  state: null,
  loading: true,
  refresh: () => Promise.resolve(),
  has: () => false,
  wasAvailable: () => false,
  lock: () => ({ allowed: false, locked: true, isTrial: false, reason: "tier" }),
});

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  // `force` bypasses the shared cache — used by the callers that just changed
  // the subscription (checkout return, plan switch, cancel). The mount call
  // below reuses the cached/in-flight response instead of firing its own.
  const refresh = useCallback(async ({ force = true } = {}) => {
    if (!user) {
      setState(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await fetchBillingMe(api, { force });
      setState(data);
    } catch {
      /* not fatal — keep last state */
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh({ force: false });
  }, [refresh]);

  const has = useCallback(
    (feature) => {
      if (!state) return false;
      return (state.features || []).includes(feature);
    },
    [state]
  );

  // True when the user's PEAK (highest-ever) tier granted this feature but
  // their CURRENT plan no longer does — i.e. they downgraded. Drives the
  // "previously available — resubscribe" indicator on locked sections.
  const wasAvailable = useCallback(
    (feature) => {
      if (!state) return false;
      return (state.previously_available_features || []).includes(feature);
    },
    [state]
  );

  // Differentiate trial-locked (show disabled) from tier-locked (hide).
  // Returns: { allowed, locked, isTrial, reason }
  //  - allowed: feature usable, render normally
  //  - locked + isTrial: render disabled with "Subscribe to unlock" hint
  //  - locked + !isTrial: hide the UI (paid tier without this feature)
  const lock = useCallback(
    (feature) => {
      const allowed = has(feature);
      const isTrial = !!state?.is_trialing;
      return {
        allowed,
        locked: !allowed,
        isTrial,
        reason: allowed ? null : isTrial ? "trial" : "tier",
      };
    },
    [has, state]
  );

  return (
    <SubscriptionContext.Provider value={{ state, loading, refresh, has, wasAvailable, lock }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  return useContext(SubscriptionContext);
}

export function trialDaysRemaining(state) {
  if (!state || !state.trial_until || !state.is_trialing) return null;
  try {
    const now = new Date();
    const until = new Date(state.trial_until);
    const ms = until - now;
    if (ms <= 0) return 0;
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
}

export function tierLabel(state) {
  if (!state) return "—";
  if (state.tier === "trial") return "Free trial";
  if (state.tier === "basic") return "Basic";
  if (state.tier === "standard") return "Standard";
  if (state.tier === "advanced") return "Advanced";
  if (state.tier === "free") return "Expired";
  // Advisor / custom plans: show the plan's friendly name, not the raw key.
  return state.plan_label || state.tier;
}

// Tiers that grant "Standard+" capabilities. Mirrors
// backend/plans.STANDARD_PLUS_TIERS — kept inline (rather than
// re-fetched) so guards render synchronously on first paint.
const STANDARD_PLUS_TIERS = new Set([
  "standard",
  "advanced",
  "advisor_starter",
  "advisor_pro",
  "advisor_seat",
]);

// True when the resolved tier qualifies as Standard+. Drives UI
// gates for paid-only capabilities (research, Focus mark-done,
// investor marketplace opt-in, etc.). Safe on null/loading state.
export function isStandardPlus(state) {
  return !!state && STANDARD_PLUS_TIERS.has(state.tier);
}

// Tiers that grant "Advanced" capabilities — the paid Advanced tier plus
// the advanced-equivalent advisor tier. Mirrors backend/plans.ADVANCED_TIERS.
// Drives the gate for Email & SMS campaigns.
const ADVANCED_TIERS = new Set(["advanced", "advisor_pro"]);

// True when the resolved tier qualifies as Advanced. Safe on null/loading.
export function isAdvanced(state) {
  return !!state && ADVANCED_TIERS.has(state.tier);
}
